import React from 'react';

import { Icon } from '@atom/mui';
import {
  AccessTimeIcon,
  AssessmentIcon,
  AttachMoneyIcon,
  CategoryIcon,
  FolderIcon,
  ForumIcon,
  GroupIcon,
  WorkIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { GrantType, PolicyAction, PolicyResource } from '@atom/types/policy';
import { Preferences } from '@atom/types/preferences';
import { showWorkListPolicyResource } from '@atom/utilities/featureToggleUtilities';
import { isInventoryReviewReceiveEnabled } from '@atom/utilities/tenantFeatureUtilities';

import { capitalSnakeCaseToTitleCase } from './stringUtilities';

export type PolicyResourceOption = {
  value: PolicyResource;
  label: string;
  icon?: React.ReactNode;
  actions: PolicyAction[];
  grantTypes?: GrantType[];
  subSourceOnly?: boolean;
};

const policyActionCustomLabels: { [key in PolicyAction]?: string } = {
  [PolicyAction.UPDATE]: 'Edit',
  [PolicyAction.BULK_UPDATE]: 'Bulk Edit',
  // [PolicyAction.UPDATE_RELATIONSHIP]: 'Edit Linked Inventory',
  [PolicyAction.CREATE_MEDIA]: 'Upload Media',
  [PolicyAction.UPDATE_MEDIA]: 'Edit Media',
  [PolicyAction.MOVE_ELEMENT]: 'Move Sub Items',
  [PolicyAction.REPLACE_ELEMENT]: 'Replace Sub Items',
};

export const getPolicyActionLabel = (action: PolicyAction) => {
  return (
    policyActionCustomLabels[action] || capitalSnakeCaseToTitleCase(action)
  );
};

const styles = {
  inventoryTypeIcon: {
    background: colors.brand.purple,
    color: colors.neutral.white,
    borderRadius: '4px',
    fontSize: '16px',
    padding: '2px',
    margin: '0 2px',
  },
  budgetPortalIcon: {
    background: colors.brand.turquoise,
    color: colors.neutral.white,
    borderRadius: '10px',
    fontSize: '20px',
    paddingLeft: '1.5px',
    paddingRight: '0.5px',
    marginRight: '2px',
  },
};

export const getSourceOptions = (preferences: Preferences) => {
  const showTimeSheet = !!preferences?.timeTracking?.timesheet;
  const showBudgeting = !!preferences?.budgeting?.hasBudgeting;
  const showTeamChat = !!preferences?.teamChat?.enabled;

  return [
    PolicyResource.INVENTORY_ITEM,
    PolicyResource.INVENTORY_TYPE,
    PolicyResource.INVENTORY_FOLDER,
    PolicyResource.WORK,
    PolicyResource.WORK_TYPE_FOLDER,
    ...(showWorkListPolicyResource ? [PolicyResource.WORK_LIST] : []),
    PolicyResource.ANALYTICS_FOLDER,
    PolicyResource.BUDGET,
    ...(showBudgeting ? [PolicyResource.BUDGETING] : []),
    ...(showTimeSheet ? [PolicyResource.TIME_SHEET] : []),
    ...(showTeamChat ? [PolicyResource.TEAM_CHAT] : []),
  ];
};

export const getResourceOptions = (
  preferences: Preferences,
): { [key: string]: PolicyResourceOption } => {
  const showMoveElement = !!preferences?.inventory?.moveElementEnabled;
  const showReplaceElement = !!preferences?.inventory?.replaceElementEnabled;
  const showAuthorizedBudget = !!preferences?.budgeting?.showAuthorizedBudget;

  return {
    [PolicyResource.INVENTORY_ITEM]: {
      value: PolicyResource.INVENTORY_ITEM,
      label: 'Inventory Item',
      icon: <Icon color={colors.brand.purple} icon={CategoryIcon} />,
      actions: [
        PolicyAction.ALL,
        PolicyAction.READ,
        PolicyAction.UPDATE,
        PolicyAction.BULK_UPDATE,
        PolicyAction.RENAME,
        PolicyAction.UPDATE_LOCATION,
        // PolicyAction.UPDATE_RELATIONSHIP,
        PolicyAction.MANAGE_INVENTORY_CHANGES,
        PolicyAction.CREATE_MEDIA,
        PolicyAction.UPDATE_MEDIA,
        PolicyAction.DELETE_MEDIA,
        ...(isInventoryReviewReceiveEnabled
          ? [PolicyAction.REVIEW, PolicyAction.RECEIVE]
          : []),
        ...(showMoveElement ? [PolicyAction.MOVE_ELEMENT] : []),
        ...(showReplaceElement ? [PolicyAction.REPLACE_ELEMENT] : []),
      ],
      grantTypes: [
        GrantType.INVENTORY_TYPE,
        GrantType.INVENTORY_FOLDER,
        GrantType.INVENTORY_ITEM,
      ],
    },
    [PolicyResource.INVENTORY_TYPE]: {
      value: PolicyResource.INVENTORY_TYPE,
      label: 'Inventory Type',
      icon: <Icon style={styles.inventoryTypeIcon} icon={CategoryIcon} />,
      actions: [PolicyAction.ALL, PolicyAction.READ],
      grantTypes: [GrantType.INVENTORY_TYPE],
    },
    [PolicyResource.INVENTORY_FOLDER]: {
      value: PolicyResource.INVENTORY_FOLDER,
      label: 'Inventory Folder',
      icon: <Icon color={colors.brand.purple} icon={FolderIcon} />,
      actions: [PolicyAction.ALL, PolicyAction.READ],
      grantTypes: [GrantType.INVENTORY_FOLDER],
    },
    [PolicyResource.WORK]: {
      value: PolicyResource.WORK,
      label: 'Work',
      icon: <Icon color={colors.brand.blue} icon={WorkIcon} />,
      actions: [
        PolicyAction.ALL,
        PolicyAction.CREATE,
        PolicyAction.BULK_CREATE,
        PolicyAction.REOPEN_CLOSED_WORK,
      ],
      grantTypes: [GrantType.WORK_TYPE_FOLDER],
    },
    [PolicyResource.WORK_TYPE_FOLDER]: {
      value: PolicyResource.WORK_TYPE_FOLDER,
      label: 'Work Template Folder',
      icon: <Icon color={colors.brand.blue} icon={FolderIcon} />,
      actions: [PolicyAction.ALL, PolicyAction.READ],
      grantTypes: [GrantType.WORK_TYPE_FOLDER],
    },
    [PolicyResource.WORK_LIST]: {
      value: PolicyResource.WORK_LIST,
      label: 'Work List',
      icon: <Icon color={colors.brand.blue} icon={WorkIcon} />,
      actions: [PolicyAction.MANAGE_COLUMN_DISPLAY],
      grantTypes: [GrantType.WORK_LIST],
    },
    [PolicyResource.ANALYTICS_FOLDER]: {
      value: PolicyResource.ANALYTICS_FOLDER,
      label: 'Analytics Folder',
      icon: <Icon color={colors.brand.green} icon={AssessmentIcon} />,
      actions: [PolicyAction.READ],
      grantTypes: [GrantType.ANALYTICS_FOLDER],
    },
    [PolicyResource.BUDGET]: {
      value: PolicyResource.BUDGET,
      label: 'User Budget',
      icon: <Icon color={colors.brand.red} icon={GroupIcon} />,
      actions: [PolicyAction.READ],
      grantTypes: [GrantType.USER],
    },
    [PolicyResource.TIME_SHEET]: {
      value: PolicyResource.TIME_SHEET,
      label: 'Timesheet',
      icon: <Icon color={colors.brand.red} icon={AccessTimeIcon} />,
      actions: [
        PolicyAction.ALL,
        PolicyAction.APPROVE,
        PolicyAction.READ,
        PolicyAction.REJECT_PENDING,
        PolicyAction.REJECT_APPROVED,
      ],
      grantTypes: [GrantType.USER_GROUP],
    },
    [PolicyResource.TEAM_CHAT]: {
      value: PolicyResource.TEAM_CHAT,
      label: 'Team Chat',
      icon: <Icon color={colors.brand.blue} icon={ForumIcon} />,
      actions: [
        PolicyAction.ALL,
        PolicyAction.MANAGE_PUBLIC_CHANNELS,
        PolicyAction.CREATE_CHANNEL,
      ],
      grantTypes: [GrantType.TEAM_CHAT],
    },
    [PolicyResource.BUDGETING]: {
      value: PolicyResource.BUDGETING,
      label: 'Budget',
      icon: (
        <Icon
          color={colors.neutral.white}
          style={styles.budgetPortalIcon}
          icon={AttachMoneyIcon}
        />
      ),
      actions: [],
      grantTypes: [
        GrantType.BUDGETING_BUDGET,
        GrantType.BUDGETING_TEMPLATE,
        GrantType.BUDGETING_UNIT,
        GrantType.BUDGETING_APPROVAL,
      ],
    },
    [PolicyResource.BUDGETING_BUDGET]: {
      subSourceOnly: true,
      value: PolicyResource.BUDGETING_BUDGET,
      label: 'Budget',
      actions: [PolicyAction.READ],
    },
    [PolicyResource.BUDGETING_TEMPLATE]: {
      subSourceOnly: true,
      value: PolicyResource.BUDGETING_TEMPLATE,
      label: 'Budget Template',
      actions: [PolicyAction.READ],
    },
    [PolicyResource.BUDGETING_APPROVAL]: {
      subSourceOnly: true,
      value: PolicyResource.BUDGETING_APPROVAL,
      label: 'Budget Approval',
      actions: [
        PolicyAction.ALL,
        PolicyAction.APPROVE,
        PolicyAction.REVERT_TO_DRAFT_FROM_SUBMITTED,
        PolicyAction.REVERT_TO_DRAFT_FROM_APPROVED,
        PolicyAction.REVERT_TO_SUBMITTED_FROM_APPROVED,
        PolicyAction.SUBMIT_DRAFT,
      ],
    },
    [PolicyResource.BUDGETING_UNIT]: {
      subSourceOnly: true,
      value: PolicyResource.BUDGETING_UNIT,
      label: 'Budget Unit',
      actions: [
        PolicyAction.ALL,
        PolicyAction.READ,
        PolicyAction.EDIT_BUDGET_ITEM,
        PolicyAction.BULK_EDIT_BUDGET_ITEM,
        ...(showAuthorizedBudget
          ? [PolicyAction.BULK_EDIT_AUTHORIZED_BUDGET]
          : []),
      ],
    },
  };
};

export const SUB_SOURCE_LABEL: {
  [key in GrantType]: string;
} = {
  [GrantType.INVENTORY_ITEM]: 'Inventory Item',
  [GrantType.INVENTORY_TYPE]: 'Inventory Type',
  [GrantType.INVENTORY_FOLDER]: 'Inventory Folder',
  [GrantType.WORK_TYPE_FOLDER]: 'Work Template Folder',
  [GrantType.WORK_LIST]: 'Work List',
  [GrantType.ANALYTICS_FOLDER]: 'Analytics Folder',
  [GrantType.BUDGETING_BUDGET]: 'Budget',
  [GrantType.BUDGETING_TEMPLATE]: 'Budget Template',
  [GrantType.BUDGETING_APPROVAL]: 'Budget Approval',
  [GrantType.BUDGETING_UNIT]: 'Budget Unit',
  [GrantType.USER]: 'User',
  [GrantType.USER_GROUP]: 'User Group',
  [GrantType.TEAM_CHAT]: 'Team Chat',
};
